/**
 * Transform optimize API channel-step output into the Channels map / conflict UI.
 */

import {
  CHANNEL_RADIO_SCOPE,
  DEFAULT_MIN_FREQ_GAP_MHZ,
  channelToFrequencyMHz,
  extractApRadiosFromDevices,
  filterMapPlottableApRadios,
  filterMarkersByBand,
  getDeviceGps,
  normalizeBand,
} from './channelMapUtils';

export const DEFAULT_SAME_CHASSIS_MIN_SEP_MHZ = 160;
export const DEFAULT_CHANNEL_BANDWIDTH_MHZ = 80;
export const DEFAULT_CHANNEL_THRESHOLD = 0.3;

/** Stable marker id — matches channelMapUtils pushApRadio key shape. */
export function channelApMarkerId(serial, radio, band, channel) {
  const s = String(serial || '').toLowerCase();
  const r = String(radio || 'wifi2').toLowerCase();
  const b = normalizeBand(band);
  return `${s}|${r}|${b}|${channel}`;
}

/** Latest channel step from an optimize result (last iteration wins). */
export function getLatestChannelStep(optimizeResult) {
  const steps = optimizeResult?.steps || [];
  const channelSteps = steps.filter((s) => s.step === 'channel');
  if (channelSteps.length === 0) return null;
  return channelSteps[channelSteps.length - 1];
}

export function filterChannelIssuesByBand(issues, bandFilter) {
  if (!Array.isArray(issues)) return [];
  if (!bandFilter || bandFilter === 'all') return issues;
  return issues.filter((issue) => {
    const devs = issue.affected_devices || [];
    return devs.some((d) => normalizeBand(d.band) === bandFilter);
  });
}

function parseCoordinatePair(coords) {
  if (!coords || typeof coords !== 'object') return null;
  const rawLat = coords.lat ?? coords.latitude;
  const rawLng = coords.lng ?? coords.longitude;
  const lat = typeof rawLat === 'number' ? rawLat : parseFloat(rawLat);
  const lng = typeof rawLng === 'number' ? rawLng : parseFloat(rawLng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
}

/** Build a map marker from a channel issue affected_device entry. */
export function markerFromChannelIssueDevice(dev, devicesBySerial) {
  if (!dev?.serial) return null;
  const serial = dev.serial;
  const band = normalizeBand(dev.band);
  const channel = Number(dev.channel);
  if (!Number.isFinite(channel) || channel <= 0) return null;

  const radioName = dev.radio || 'wifi2';
  let gps = parseCoordinatePair(dev.coordinates);
  if (!gps) {
    gps = getDeviceGps(devicesBySerial.get(String(serial).toLowerCase()));
  }

  const frequencyMHz = Number(dev.frequency_mhz)
    || Math.round(channelToFrequencyMHz(band, channel));

  return {
    id: channelApMarkerId(serial, radioName, band, channel),
    serial,
    serialSuffix: serial.slice(-4),
    hasGps: Boolean(gps),
    lat: gps?.lat ?? null,
    lng: gps?.lng ?? null,
    channel,
    band,
    radioName,
    frequencyMHz,
    channelWidth: null,
    isCap: Boolean(dev.is_cap),
    boundToParent: Boolean(dev.bound_to_parent),
    role: 'ap',
    plannerScope: true,
  };
}

/** Collect unique AP markers referenced by channel issues. */
export function buildConflictMarkersFromIssues(issues, devices) {
  const devicesBySerial = new Map();
  (devices || []).forEach((d) => {
    if (d?.serial_number) devicesBySerial.set(String(d.serial_number).toLowerCase(), d);
  });

  const markers = [];
  const seen = new Set();
  (issues || []).forEach((issue) => {
    (issue.affected_devices || []).forEach((dev) => {
      const marker = markerFromChannelIssueDevice(dev, devicesBySerial);
      if (!marker || seen.has(marker.id)) return;
      seen.add(marker.id);
      markers.push(marker);
    });
  });
  return markers;
}

/** Conflict marker ids for map highlighting. */
export function buildConflictIdsFromIssues(issues) {
  const conflictIds = new Set();
  (issues || []).forEach((issue) => {
    (issue.affected_devices || []).forEach((dev) => {
      if (!dev?.serial || !dev?.channel) return;
      conflictIds.add(channelApMarkerId(dev.serial, dev.radio, dev.band, dev.channel));
    });
  });
  return conflictIds;
}

function endpointFromIssueDevice(dev) {
  return {
    serial: dev.serial,
    band: normalizeBand(dev.band),
    channel: dev.channel,
    frequencyMHz: dev.frequency_mhz || Math.round(channelToFrequencyMHz(dev.band, dev.channel)),
    radio: dev.radio,
    isCap: dev.is_cap,
    boundToParent: dev.bound_to_parent,
  };
}

/** Human-readable kind for a planner channel issue id. */
export function channelIssueKind(issueId) {
  const id = String(issueId || '');
  if (id.includes('-unsatisfied')) return 'Unsatisfied';
  if (id.includes('-heard-isolation')) return 'Heard isolation';
  return 'Overlap';
}

function pairKeyFromEndpoints(a, b, band) {
  const serials = [String(a?.serial || '').toLowerCase(), String(b?.serial || '').toLowerCase()]
    .filter(Boolean)
    .sort();
  if (serials.length < 2) return String(a?.serial || b?.serial || '');
  return `${serials[0]}|${serials[1]}|${normalizeBand(band || a?.band || b?.band)}`;
}

export function pairKeyFromConflictCard(card) {
  if (!card) return '';
  return pairKeyFromEndpoints(card.a, card.b, card.a?.band || card.b?.band);
}

/** Merge planner cards that describe the same AP pair on the same band. */
export function mergeConflictCardsByPair(cards) {
  const byPair = new Map();
  (cards || []).forEach((card) => {
    const key = pairKeyFromConflictCard(card);
    if (!key) return;
    const existing = byPair.get(key);
    if (!existing) {
      byPair.set(key, {
        ...card,
        pairKey: key,
        issueKinds: card.issueKind ? [card.issueKind] : [],
        descriptions: card.description ? [card.description] : [],
        sourceIssueIds: card.id ? [card.id] : [],
      });
      return;
    }
    if (card.issueKind && !existing.issueKinds.includes(card.issueKind)) {
      existing.issueKinds.push(card.issueKind);
    }
    if (card.description && !existing.descriptions.includes(card.description)) {
      existing.descriptions.push(card.description);
    }
    if (card.id && !existing.sourceIssueIds.includes(card.id)) {
      existing.sourceIssueIds.push(card.id);
    }
    if ((card.severityScore ?? 0) > (existing.severityScore ?? 0)) {
      existing.severityScore = card.severityScore;
      existing.severity = card.severity;
    }
  });
  return [...byPair.values()];
}

/** Map a channel issue to the conflict card shape used by ChannelsPanel. */
export function issueToConflictCard(issue, minFreqGapMHz) {
  const devices = issue.affected_devices || [];
  if (devices.length < 2) return null;
  const [a, b] = devices;
  return {
    id: issue.id,
    issueKind: channelIssueKind(issue.id),
    description: issue.description,
    severity: issue.severity,
    severityScore: issue.severity_score,
    neighborEvidence: issue.neighbor_evidence,
    bestRssiDbm: issue.neighbor_rssi_dbm,
    freqSepMHz: issue.frequency_difference_mhz,
    minGapMHz: minFreqGapMHz,
    distanceM: issue.distance_meters,
    involvesCap: issue.involves_cap,
    unsatisfied: String(issue.id || '').includes('-unsatisfied'),
    suggestedActions: issue.suggested_actions || [],
    a: endpointFromIssueDevice(a),
    b: endpointFromIssueDevice(b),
  };
}

export function partitionChannelIssues(issues) {
  const planner = [];
  const unsatisfied = [];
  (issues || []).forEach((issue) => {
    if (String(issue.id || '').includes('-unsatisfied')) {
      unsatisfied.push(issue);
    } else {
      planner.push(issue);
    }
  });
  return { plannerIssues: planner, unsatisfiedIssues: unsatisfied };
}

export function formatChannelPlanStatsSummary(stats) {
  if (!stats) return null;
  const excluded = stats.idle_ap_devices_excluded || [];
  const kept = stats.idle_ap_dual_radio_cochannel_kept || [];
  switch (stats.idle_ap_retry_outcome) {
    case 'disabled':
      return 'Idle AP exclusion disabled — all AP radios stay in the planner graph.';
    case 'no_idle_devices':
      return 'No idle AP devices — every AP serves mesh children or is on the reparent whitelist.';
    case 'primary_excluded':
      if (kept.length > 0) {
        return `Excluded ${excluded.length} idle AP device(s); kept ${kept.length} dual-radio co-channel node(s) in the graph.`;
      }
      return `Excluded ${excluded.length} unused AP device(s) from heard/conflict planning.`;
    case 'primary_excluded_unsatisfied_remain':
      return `Excluded ${excluded.length} idle AP(s); ${stats.unsatisfied_pairs_final ?? '?'} unsatisfied heard-pair(s) remain among active APs.`;
    default:
      if (stats.idle_ap_retry_enabled) return 'Idle AP graph trimming active.';
      return null;
  }
}

/** Request body for channel-only optimize analysis (Channels tab). */
export function buildChannelOptimizeRequest({
  neighborRssiFloorDbm,
  minFrequencySeparationMHz = DEFAULT_MIN_FREQ_GAP_MHZ,
  sameChassisMinSepMHz = DEFAULT_SAME_CHASSIS_MIN_SEP_MHZ,
  channelBandwidth = DEFAULT_CHANNEL_BANDWIDTH_MHZ,
  threshold = DEFAULT_CHANNEL_THRESHOLD,
  regulatoryDomain = 'default',
  capOnlyActions = false,
  excludeIdleAPs = true,
  customTreeIds = [],
} = {}) {
  const treeIds = (customTreeIds || []).map((id) => String(id || '').trim()).filter(Boolean);
  return {
    max_iterations: 1,
    enabled_steps: ['channel'],
    use_health_focus: false,
    rebuild_tree: false,
    channel_bandwidth: channelBandwidth,
    threshold,
    min_frequency_separation_mhz: minFrequencySeparationMHz,
    same_chassis_min_frequency_separation_mhz: sameChassisMinSepMHz,
    // Disable the global accept gate so the tab shows the planner's
    // proposed moves even when net fleet improvement is below 5%.
    channel_global_accept_floor: -1,
    channel_improvement_floor: 0,
    neighbor_rssi_floor_dbm: neighborRssiFloorDbm,
    regulatory_domain: regulatoryDomain,
    cap_only_actions: capOnlyActions,
    ...(excludeIdleAPs ? {} : { idle_ap_retry_disabled: true }),
    ...(treeIds.length === 1 ? { custom_tree_id: treeIds[0] } : {}),
    ...(treeIds.length > 1 ? { custom_tree_ids: treeIds } : {}),
  };
}

export function channelAnalysisScopeHelpText(selectedCustomTrees = []) {
  if (!selectedCustomTrees?.length) {
    return 'Whole-site channel analysis: all devices and neighbour data feed the planner. Select custom trees below to scope analysis to an isolated cohort (display only — apply moves via Optimize).';
  }
  if (selectedCustomTrees.length === 1) {
    return 'Isolated cohort: only this custom tree is loaded into the planner. Neighbour scans are required for its Nodes only; the rest of the site is ignored.';
  }
  return `Isolated cohort: ${selectedCustomTrees.length} custom trees are unioned into one planner world. Neighbour scans are required for those Nodes only; the rest of the site is ignored.`;
}

/**
 * Build map + conflict view-model from optimize output.
 */
export function buildChannelOptimizeViewModel({
  optimizeResult,
  devices,
  bandFilter,
  minFreqGapMHz,
}) {
  const channelStep = getLatestChannelStep(optimizeResult);
  const allIssues = channelStep?.channel_issues || [];
  const filteredIssues = filterChannelIssuesByBand(allIssues, bandFilter);
  const { plannerIssues, unsatisfiedIssues } = partitionChannelIssues(filteredIssues);

  const conflictIds = buildConflictIdsFromIssues(filteredIssues);
  const issueMarkers = buildConflictMarkersFromIssues(filteredIssues, devices);

  const { apRadios: allApRadios, skippedNoApRadios } = extractApRadiosFromDevices(
    devices,
    { scope: CHANNEL_RADIO_SCOPE.OPTIMIZER },
  );
  const bandApRadios = filterMarkersByBand(allApRadios, bandFilter);
  const mapPlottableApRadios = filterMapPlottableApRadios(bandApRadios);

  // Merge fleet AP markers with issue endpoints (issue GPS wins when present).
  const markerById = new Map();
  mapPlottableApRadios.forEach((m) => markerById.set(m.id, m));
  issueMarkers.forEach((m) => {
    if (!m.hasGps) return;
    markerById.set(m.id, { ...markerById.get(m.id), ...m });
  });

  const mapMarkers = [...markerById.values()];
  const rawConflictCards = plannerIssues
    .map((issue) => issueToConflictCard(issue, minFreqGapMHz))
    .filter(Boolean);
  const conflictCards = mergeConflictCardsByPair(rawConflictCards);
  const unsatisfiedCards = unsatisfiedIssues
    .map((issue) => issueToConflictCard(issue, minFreqGapMHz))
    .filter(Boolean);

  return {
    channelStep,
    plannerIssues,
    unsatisfiedIssues,
    conflictCards,
    unsatisfiedCards,
    conflictIds,
    mapMarkers,
    mapPlottableApRadios,
    bandApRadios,
    skippedNoApRadios,
    channelActions: channelStep?.channel_actions || [],
    planStats: channelStep?.channel_plan_stats || null,
    planStatsText: formatChannelPlanStatsSummary(channelStep?.channel_plan_stats),
    score: channelStep?.score || null,
    noActionSummary: channelStep?.channel_plan_stats?.no_action_summary || null,
  };
}

export function describeChannelAnalyzeEmptyMap({
  hasAnalysis,
  mapMarkerCount,
  showOverlappingOnly,
  hasAnalyzedApRadios,
}) {
  if (!hasAnalysis) {
    return 'Run channel analysis to populate the map from the optimize planner.';
  }
  if (!hasAnalyzedApRadios) {
    return 'No jnet2 AP radios with GPS to plot on the map.';
  }
  if (showOverlappingOnly && mapMarkerCount === 0) {
    return 'No planner conflicts with GPS at the current settings. Switch to “All APs” to see every backhaul radio.';
  }
  return '';
}
